import { useCallback, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Icon } from '@iconify/react';
import './FileAttachmentPanel.scss';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

export interface UploadedFileInfo {
  tempFileId: string;
  fileName: string;
  size: number;
}

export interface FileAttachmentPanelProps {
  files: UploadedFileInfo[];
  onFilesChange: (files: UploadedFileInfo[]) => void;
  uploadFile: (file: File) => Promise<UploadedFileInfo>;
  deleteFile: (tempFileId: string) => Promise<void>;
  disabled?: boolean;
  maxFileSize?: number;
  acceptedExtensions?: string[];
  label?: string;
  uploadLabel?: string;
  emptyMessage?: string;
}

function getErrorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { message?: string } }; message?: string };
  return candidate?.response?.data?.message || candidate?.message || fallback;
}

export default function FileAttachmentPanel({
  files,
  onFilesChange,
  uploadFile,
  deleteFile,
  disabled,
  maxFileSize = MAX_FILE_SIZE,
  acceptedExtensions = ACCEPTED_EXTENSIONS,
  label = 'Archivos adjuntos',
  uploadLabel = 'Adjuntar archivo',
  emptyMessage = 'Sin archivos adjuntos',
}: FileAttachmentPanelProps) {
  const toast = useRef<Toast>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = event.target.files;
      if (!selectedFiles) return;

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();

        if (!acceptedExtensions.includes(ext)) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Formato no soportado',
            detail: `${file.name}: solo se aceptan ${acceptedExtensions.map(e => e.replace('.', '').toUpperCase()).join(', ')}`,
            life: 4000,
          });
          continue;
        }

        if (file.size > maxFileSize) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Archivo demasiado grande',
            detail: `${file.name}: el maximo es ${(maxFileSize / (1024 * 1024)).toFixed(0)} MB`,
            life: 4000,
          });
          continue;
        }

        try {
          const uploaded = await uploadFile(file);
          onFilesChange([...files, uploaded]);
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error al subir archivo',
            detail: getErrorMessage(error, 'No se pudo subir el archivo'),
            life: 4000,
          });
        }
      }

      if (inputRef.current) inputRef.current.value = '';
    },
    [files, onFilesChange, uploadFile, acceptedExtensions, maxFileSize],
  );

  const handleDelete = useCallback(
    async (tempFileId: string) => {
      try {
        await deleteFile(tempFileId);
      } catch {
        // ignore delete error on server, remove locally anyway
      }
      onFilesChange(files.filter((f) => f.tempFileId !== tempFileId));
    },
    [files, onFilesChange, deleteFile],
  );

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const acceptAttr = acceptedExtensions.join(',');

  return (
    <div className="file-attachment-panel">
      <Toast ref={toast} />
      <div className="file-attachment-panel__header">
        <span className="file-attachment-panel__label">
          <Icon icon="mdi:paperclip" style={{ fontSize: '0.9rem' }} />
          {label}
        </span>
        <Button
          className="p-button-outlined p-button-sm file-attachment-panel__upload-btn"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <Icon icon="mdi:upload" style={{ fontSize: '0.9rem' }} />
          <span>{uploadLabel}</span>
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>

      {files.length > 0 ? (
        <ul className="file-attachment-panel__list">
          {files.map((f) => (
            <li key={f.tempFileId} className="file-attachment-panel__item">
              <Icon
                icon={f.fileName.toLowerCase().endsWith('.pdf') ? 'mdi:file-pdf-box' : 'mdi:file-image'}
                className="file-attachment-panel__item-icon"
              />
              <span className="file-attachment-panel__item-name">{f.fileName}</span>
              <span className="file-attachment-panel__item-size">{formatSize(f.size)}</span>
              <Button
                icon="pi pi-trash"
                className="p-button-text p-button-rounded p-button-danger p-button-sm file-attachment-panel__item-delete"
                onClick={() => handleDelete(f.tempFileId)}
                aria-label="Eliminar archivo"
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="file-attachment-panel__empty">{emptyMessage}</p>
      )}
    </div>
  );
}
